/* eslint-disable no-case-declarations */

import React, { forwardRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { ControlledCheckbox } from 'src/components/ui/Checkbox/Checkbox';
import Select, { ControlledSelect, Option } from 'src/components/ui/Select/Select';
import { Connector } from 'src/services/connector';
import { t } from 'i18next';
import { ControlledInput } from '../../ui/Input/Input';
import { MOCK_API_VERSIONS } from './apiVersionMockData';

type Props = {
  connector?: Connector;
  onSubmit?: (data: any) => void;
  allowEdition?: boolean;
};

export type ConnectorFieldType = 'text' | 'password' | 'number' | 'checkbox' | 'select' | 'multiselect';

export interface ConnectorOption {
  name: string;
  value: string;
}

export interface ConnectorField {
  name: string;
  title: string;
  type: ConnectorFieldType;
  value: unknown;
  required: boolean;
  options?: ConnectorOption[] | null;
  sequence?: number | null;
  block?: string | null;
}

export const IntegrationSetupForm = forwardRef<HTMLFormElement, Props>(({ onSubmit, connector, allowEdition }, ref) => {
  const dynamicSchema: ZodType<any> = useMemo(() => {
    if (!connector?.template?.specific_fields) return z.object({});

    const schemaObj: Record<string, z.ZodTypeAny> = {};

    connector.template.specific_fields.forEach((field: ConnectorField) => {
      let fieldSchema: z.ZodTypeAny;

      switch (field.type) {
        case 'checkbox':
          fieldSchema = z.boolean();
          break;

        case 'number':
          fieldSchema = z.preprocess(val => (val === '' || val === null ? undefined : Number(val)), z.number());
          break;

        case 'multiselect':
          fieldSchema = z.array(z.string());
          break;

        default:
          fieldSchema = z.string().trim();
          break;
      }

      if (!field.required) {
        fieldSchema = fieldSchema.optional().or(z.literal(''));
      } else {
        if (field.type === 'password' && typeof field.value === 'string' && field.value.startsWith('*')) {
          fieldSchema = z.string();
        } else {
          if (fieldSchema instanceof z.ZodString) {
            fieldSchema = fieldSchema.min(1, `${field.title} is required`);
          }
        }
      }

      schemaObj[field.name] = fieldSchema;
    });

    return z.object(schemaObj);
  }, [connector]);

  type FormValues = z.infer<typeof dynamicSchema>;

  const { handleSubmit, control } = useForm<FormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: useMemo(
      () =>
        connector?.template?.specific_fields?.reduce(
          (acc, field) => ({
            ...acc,
            [field.name as string]: (field.value as any) ?? '',
          }),
          {} as Record<string, any>,
        ),
      [connector],
    ),
  });

  const renderField = (field: any, inputProps: any) => {
    switch (field.type) {
      case 'password':
        return (
          <ControlledInput
            {...inputProps}
            control={control}
            secret
            type={field.type}
            label={field.title}
            placeholder=""
            disabled={!allowEdition}
          />
        );
      case 'text':
        return (
          <ControlledInput
            {...inputProps}
            control={control}
            type={field.type}
            label={field.title}
            placeholder=""
            disabled={!allowEdition}
          />
        );
      case 'number':
        return (
          <ControlledInput
            disabled={!allowEdition}
            {...inputProps}
            control={control}
            type={field.type}
            label={field.title}
            placeholder=""
          />
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-x-8">
            <ControlledCheckbox
              disabled={!allowEdition}
              control={control}
              name={field.name}
              defaultValue={field.value ?? false}
            />
            <span className="typo-text-14"> {field.title}</span>
          </div>
        );
      case 'select':
        const selectOptions = field.options.map((option: any) => ({
          label: option.value,
          value: option.name,
        }));
        const selectedOption = selectOptions.find((opt: any) => opt.value === inputProps.value);
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: { onChange } }) => (
              <Select
                options={field.options.map((option: any) => ({
                  label: option.value,
                  value: option.name,
                }))}
                defaultValue={selectedOption}
                isClearable
                onChange={(selected: any) => {
                  onChange(selected ? selected.value : '');
                }}
                disabled={!allowEdition}
              />
            )}
          />
        );
      case 'multiselect':
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                isMultiSelect
                disabled={!allowEdition}
                options={field.options.map((option: any) => ({
                  label: option.value,
                  value: option.name,
                }))}
                defaultValue={
                  Array.isArray(value)
                    ? value.map((val: any) => field.options.find((option: any) => option.name === val))
                    : []
                }
                onChange={(selected: any) => {
                  onChange(selected ? selected.map((s: any) => s.value) : []);
                }}
                isClearable
              />
            )}
          />
        );
      default:
        return (
          <ControlledInput
            {...inputProps}
            control={control}
            type="text"
            label={field.title}
            placeholder=""
            disabled={!allowEdition}
          />
        );
    }
  };

  const groupedFields = useMemo(() => {
    const fields = connector?.template?.specific_fields ?? [];

    const groups: Record<string, any> = {};
    const ungrouped: any[] = [];

    fields.forEach(field => {
      if (field.block) {
        if (!groups[field.block]) groups[field.block] = [];
        groups[field.block].push(field);
      } else {
        ungrouped.push(field);
      }
    });

    return { groups, ungrouped };
  }, [connector]);

  return (
    <form
      ref={ref}
      onSubmit={e => {
        e.preventDefault();
        handleSubmit(
          data => {
            if (onSubmit) {
              const filteredData = Object.fromEntries(
                Object.entries(data).filter(([key, value]) => {
                  const originalField = connector?.template?.specific_fields?.find(f => f.name === key);
                  return originalField && originalField.value !== value;
                }),
              );
              onSubmit(filteredData);
            }
          },
          errors => {
            console.error('❌ Validation failed:', errors);
          },
        )(e);
      }}
      className="flex flex-col flex-1 gap-16">
      <div className="flex flex-col w-full gap-[16px]">
        {Object.entries(groupedFields.groups).map(([groupName, fields]) => {
          const external = fields.find((f: any) => f.name.toLowerCase().includes('external'));
          const internal = fields.find((f: any) => f.name.toLowerCase().includes('internal'));

          return (
            <div key={groupName} className="flex flex-col gap-[12px]">
              <span className="typo-text-14--medium text-faded">{groupName}</span>
              <div className="flex flex-row gap-[16px]">
                {external && (
                  <div className="flex-1">
                    <span className="typo-text-14--medium text-faded">{external.title}</span>
                    <Controller
                      name={external.name}
                      control={control}
                      render={({ field: { ref, ...inputProps } }) => renderField(external, inputProps)}
                    />
                  </div>
                )}
                {internal && (
                  <div className="flex-1">
                    <span className="typo-text-14--medium text-faded">{internal.title}</span>
                    <Controller
                      name={internal.name}
                      control={control}
                      render={({ field: { ref, ...inputProps } }) => renderField(internal, inputProps)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {groupedFields.ungrouped.map(field => (
          <div key={field.name} className="col-span-2">
            <span className="typo-text-14--medium text-faded">{field.title}</span>
            <Controller
              name={field.name}
              control={control}
              render={({ field: { ref, ...inputProps } }) => {
                if (field.name === 'api_version') {
                  return (
                    <ControlledSelect
                      control={control}
                      {...inputProps}
                      options={MOCK_API_VERSIONS as unknown as Option[]}
                      containerClassName="col-span-2"
                      label={field.title}
                      placeholder={t('general.select')}
                      disabled={!allowEdition}
                      isMultiSelect={false}
                    />
                  );
                }

                return (
                  <ControlledInput
                    control={control}
                    {...inputProps}
                    label={field.title}
                    placeholder=""
                    disabled={!allowEdition}
                    type={field.type === 'password' ? 'password' : 'text'}
                  />
                );
              }}
            />
          </div>
        ))}
      </div>
    </form>
  );
});
